import { AlertTriangle, HelpCircle, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const CLOSE_DURATION = 200;

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  danger?: boolean;
  onConfirm: (e?: React.MouseEvent) => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmText = "Подтвердить",
  cancelText = "Отмена",
  loading = false,
  danger = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [mounted, setMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    window.setTimeout(onClose, CLOSE_DURATION);
  };

  useEffect(() => {
    if (!open) return;
    setMounted(false);
    setIsClosing(false);
    const raf = requestAnimationFrame(() => setMounted(true));
    const focusT = window.setTimeout(() => confirmBtnRef.current?.focus(), 120);

    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(focusT);
      document.removeEventListener("keydown", onKey);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      window.scrollTo(0, scrollY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const shown = mounted && !isClosing;
  const Icon = danger ? AlertTriangle : HelpCircle;

  // Render at <body> via a portal: callers mount this dialog *inside* a card
  // (e.g. the agent card), and that card animates with `translate-y` on hover.
  // A CSS transform on an ancestor turns `position: fixed` into "fixed relative
  // to the card" — so without the portal the overlay gets trapped and clipped
  // inside the card instead of covering the screen.
  return createPortal(
    <div
      className={`fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm transition-opacity duration-200 ${
        shown ? "opacity-100" : "opacity-0"
      }`}
      onClick={handleClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-slate-900/20 overflow-hidden
          transition-transform duration-300 ease-out
          ${shown ? "translate-y-0 sm:scale-100" : "translate-y-full sm:translate-y-0 sm:scale-95"}`}
      >
        {/* drag handle on mobile */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="h-1 w-9 rounded-full bg-slate-200" />
        </div>

        <button
          onClick={handleClose}
          aria-label={cancelText}
          className="absolute right-3.5 top-3.5 hidden h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 sm:flex"
        >
          <X size={16} />
        </button>

        <div className="px-6 pb-6 pt-5 sm:pt-8">
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${
              danger
                ? "bg-gradient-to-br from-rose-50 to-red-100 text-rose-500"
                : "bg-gradient-to-br from-indigo-50 to-blue-100 text-indigo-500"
            }`}
          >
            <Icon size={26} />
          </div>

          <h2
            id="confirm-dialog-title"
            className="text-center text-lg font-black text-slate-900"
          >
            {title}
          </h2>

          {description && (
            <p className="mx-auto mt-2 max-w-xs text-center text-sm leading-relaxed text-slate-500">
              {description}
            </p>
          )}

          <div className="mt-6 flex gap-2.5">
            <button
              onClick={handleClose}
              disabled={loading}
              className="h-11 flex-1 rounded-2xl border border-slate-200 text-sm font-bold text-slate-600 transition hover:bg-slate-50 active:scale-[0.98] disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              ref={confirmBtnRef}
              onClick={(e) => onConfirm(e)}
              disabled={loading}
              className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-2xl text-sm font-bold text-white shadow-lg transition active:scale-[0.98] disabled:opacity-60 ${
                danger
                  ? "bg-gradient-to-r from-rose-500 to-red-500 shadow-rose-500/25 hover:from-rose-600 hover:to-red-600"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-indigo-500/25 hover:from-blue-500 hover:to-indigo-500"
              }`}
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
